import {
  Box,
  Button,
  Switch,
  Text,
  Flex,
  VStack,
  useToast,
  Divider
} from '@chakra-ui/react';
import { FC, useState, useEffect, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { seasonChallengeTaskSchema, SeasonChallengeTaskFormData } from '@olagg/validation-schemas/seasonChallengeTask.schema';
import { AdminSeasonChallengeTask } from '@olagg/db-types';
import OlaGGFormInput from '../../../../components/Forms/OlaggFormInput';
import OlaggForm from '../../../../components/Forms/OlaggForm';
import OlaggFormSelect from '../../../../components/Forms/OlaggFormSelect';
import useSeasonChallengeTasks from './hooks/useSeasonChallengeTasks';

type IProps = {
  initialData?: AdminSeasonChallengeTask | undefined;
  onSubmit: (data: SeasonChallengeTaskFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  isEdit?: boolean;
};

// Configuración genérica para los selectores
type OptionLoaderConfig = {
  key: string;
  label: string;
  placeholder: string;
  actionTypes: string[];
  initialParams: Record<string, any>;
  searchParams: (inputValue?: string) => Record<string, any>;
  fetchFunction: (params: any) => Promise<any>;
  required?: boolean;
};

const TaskForm: FC<IProps> = ({ initialData, onSubmit, onCancel, loading = false, isEdit = false }) => {
  const { getActiveQuests, getTournaments, getSurveys, getGames, getTokens, getEvents } = useSeasonChallengeTasks();
  const toast = useToast();
  const [otherLangs, setOtherLangs] = useState<boolean>(
    !!(initialData?.extraData?.en || initialData?.extraData?.pt)
  );
  
  // Estado genérico para todas las opciones
  const [optionsState, setOptionsState] = useState<Record<string, {value: string, label: string}[]>>({});

  // Función genérica para formatear respuestas
  const formatOptionsResponse = useCallback((response: any[], useNumericalId: boolean = false, useTokenId: boolean = false, useLocalizedTitle: boolean = false): {value: string, label: string}[] => {
    if (!Array.isArray(response)) return [];
    if (useTokenId) {
      return response.map((item: any) => ({
        value: String(item.tokenId),
        label: item.name || 'Untitled'
      }));
    }
    return response.map((item: any) => {
      const value = useNumericalId ? String(item.numericalId) : String(item.id);
      let label = 'Untitled';
      
      if (useLocalizedTitle && item.title?.es) {
        // Para eventos, title es un objeto con idiomas
        label = item.title.es;
      } else {
        // Para otros recursos, title es un string
        label = item.title || item.name || 'Untitled';
      }
      
      return { value, label };
    });
  }, []);

  // Funciones de búsqueda asíncrona específicas (para evitar recreaciones)
  const loadQuestOptions = useCallback(async (inputValue: string) => {
    try {
      const params = { 
        visible: "true" as const, 
        page: 1, 
        limit: 50, 
        title: inputValue || undefined 
      };
      const response = await getActiveQuests(params);
      const formatted = formatOptionsResponse(response, true); // Las quests usan numericalId
      return formatted;
    } catch (error) {
      console.error('Error loading quest options:', error);
      return [];
    }
  }, [getActiveQuests, formatOptionsResponse]);

  const loadTournamentOptions = useCallback(async (inputValue: string) => {
    try {
      const params = { 
        page: 1, 
        limit: 50, 
        title: inputValue || undefined 
      };
      const response = await getTournaments(params);
      return formatOptionsResponse(response, false); // Los tournaments usan id
    } catch (error) {
      console.error('Error loading tournament options:', error);
      return [];
    }
  }, [getTournaments, formatOptionsResponse]);

  const loadSurveyOptions = useCallback(async (inputValue: string) => {
    try {
      const params = { 
        page: 1, 
        limit: 50, 
        title: inputValue || undefined 
      };
      const response = await getSurveys(params);
      return formatOptionsResponse(response, false);
    } catch (error) {
      console.error('Error loading survey options:', error);
      return [];
    }
  }, [getSurveys, formatOptionsResponse]);

  const loadGameOptions = useCallback(async (inputValue: string) => {
    try {
      const params = {
        page: 1,
        limit: 50,
        title: inputValue || undefined
      };
      const response = await getGames(params);
      return formatOptionsResponse(response, false);
    } catch (error) {
      console.error('Error loading game options:', error);
      return [];
    }
  }, [getGames, formatOptionsResponse]);

  const loadTokenOptions = useCallback(async (inputValue: string) => {
    try {
      const params = {
        page: 1,
        limit: 50,
        name: inputValue || undefined
      };
      const response = await getTokens(params);
      return formatOptionsResponse(response, false);
    } catch (error) {
      console.error('Error loading token options:', error);
      return [];
    }
  }, [getTokens, formatOptionsResponse]);

  const loadEventOptions = useCallback(async (inputValue: string) => {
    try {
      const params = {
        page: 1,
        limit: 50,
        title: inputValue || undefined
      };
      const response = await getEvents(params);
      return formatOptionsResponse(response, true, false, true); // Los eventos usan numericalId y title localizado
    } catch (error) {
      console.error('Error loading event options:', error);
      return [];
    }
  }, [getEvents, formatOptionsResponse]);

  // Configuración de los selectores (simplificada)
  const getSelectorConfig = useCallback((actionType: string) => {
    switch (actionType) {
      case 'quest':
        return {
          key: 'quest',
          label: 'Quest Objetivo (Opcional)',
          placeholder: 'Selecciona una quest...',
          asyncOptions: loadQuestOptions,
          required: false,
          targetType: 'Quest'
        };
      case 'tournament_participation':
      case 'tournament_win':
        return {
          key: 'tournament',
          label: 'Torneo Objetivo (Opcional)',
          placeholder: 'Selecciona un torneo...',
          asyncOptions: loadTournamentOptions,
          required: false,
          targetType: 'Tournament'
        };
      case 'survey':
        return {
          key: 'survey',
          label: 'Encuesta Objetivo (Opcional)',
          placeholder: 'Selecciona una encuesta...',
          asyncOptions: loadSurveyOptions,
          required: false,
          targetType: 'Survey'
        };
      case 'game_rating':
        return {
          key: 'game',
          label: 'Juego Objetivo (Opcional)',
          placeholder: 'Selecciona un juego...',
          asyncOptions: loadGameOptions,
          required: false,
          targetType: 'Game'
        };
      case 'badge_claim':
        return {
          key: 'badge',
          label: 'Insignia Objetivo (Opcional)',
          placeholder: 'Selecciona una insignia...',
          asyncOptions: loadTokenOptions,
          required: false,
          targetType: 'Token'
        };
      case 'event_notification':
        return {
          key: 'event',
          label: 'Evento Objetivo (Opcional)',
          placeholder: 'Selecciona un evento...',
          asyncOptions: loadEventOptions,
          required: false,
          targetType: 'Event'
        };
      default:
        return null;
    }
  }, [loadQuestOptions, loadTournamentOptions, loadEventOptions]);

  // Cargar todas las opciones iniciales al montar el componente - SOLO UNA VEZ
  useEffect(() => {
    const loadAllInitialOptions = async () => {
      try {        
        // Cargar quests (usan numericalId)
        const questsResponse = await getActiveQuests({ visible: "true" as const, page: 1, limit: 100 });
        const formattedQuests = formatOptionsResponse(questsResponse, true, false); // true para numericalId

        // Cargar tournaments (usan id)
        const tournamentsResponse = await getTournaments({ page: 1, limit: 100 });
        const formattedTournaments = formatOptionsResponse(tournamentsResponse, false, false); // false para id

        const surveysResponse = await getSurveys({ page: 1, limit: 100 });
        const formattedSurveys = formatOptionsResponse(surveysResponse, false, false); // false para id

        const gamesResponse = await getGames({ page: 1, limit: 100 });
        const formattedGames = formatOptionsResponse(gamesResponse, false, false); // false para id

        const tokensResponse = await getTokens({ page: 1, limit: 100 });
        const formattedTokens = formatOptionsResponse(tokensResponse, false, true); // false para id

        const eventsResponse = await getEvents({ page: 1, limit: 100 });
        const formattedEvents = formatOptionsResponse(eventsResponse, true, false, true); // true para numericalId, true para title localizado

        setOptionsState({
          quest: formattedQuests,
          tournament: formattedTournaments,
          survey: formattedSurveys,
          game: formattedGames,
          badge: formattedTokens,
          event: formattedEvents,
        });
      } catch (error) {
        console.error('Error loading initial options:', error);
      }
    };

    loadAllInitialOptions();
  }, []);

  const actionTypeOptions = [
    { value: 'quest', label: 'Quest' },
    { value: 'tournament_participation', label: 'Participación en Torneo' },
    { value: 'tournament_win', label: 'Victoria en Torneo' },
    { value: 'survey', label: 'Encuesta' },
    { value: 'treasure_play', label: 'Jugar Tesoro' },
    { value: 'treasure_win', label: 'Ganar Tesoro' },
    { value: 'streak', label: 'Racha' },
    { value: 'news_vote', label: 'Voto de Noticias' },
    { value: 'game_rating', label: 'Rating de Juego' },
    { value: 'referral', label: 'Referido' },
    { value: 'badge_claim', label: 'Reclamar Insignia' },
    { value: 'shop_purchase', label: 'Compra en Tienda' },
    { value: 'event_notification', label: 'Notificación de Evento' },
  ];

  const useFormMethods = useForm({
    resolver: yupResolver(seasonChallengeTaskSchema),
    mode: 'onChange' as const,
    defaultValues: {
      title: initialData?.title || '',
      actionType: (initialData?.actionType as any) || 'quest',
      actionParams: initialData?.actionParams || {},
      targetType: initialData?.targetType || '',
      targetId: initialData?.targetId ? String(initialData.targetId) : undefined,
      streakDays: initialData?.actionParams?.days ? String(initialData.actionParams.days) : undefined,
      minAmount: initialData?.actionParams?.minAmount ? String(initialData.actionParams.minAmount) : undefined,
      link: initialData?.link || '',
      articleId: initialData?.actionParams?.articleId || undefined,
      extra_data: {
        en: {
          title: initialData?.extraData?.en?.title || ''
        },
        pt: {
          title: initialData?.extraData?.pt?.title || ''
        }
      }
    }
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch,
    reset,
    getValues
  } = useFormMethods;

  // Agregar la opción actual del initialData si existe y no está en las opciones
  useEffect(() => {
    if (isEdit && initialData?.targetInfo && initialData?.actionType && initialData?.targetId) {
      // Ahora es mucho más simple: todos usan targetId directamente
      const currentOption = {
        value: String(initialData.targetId),
        label: initialData.targetInfo.title?.es ? initialData.targetInfo.title.es : initialData.targetInfo.title || 'Untitled'
      };
      
      // Determinar qué tipo de selector necesita la opción actual
      let targetKey: string | null = null;
      
      if (initialData.actionType === 'quest') {
        targetKey = 'quest';
      } else if (['tournament_participation', 'tournament_win'].includes(initialData.actionType)) {
        targetKey = 'tournament';
      } else if (initialData.actionType === 'news_vote') {
        targetKey = 'news';
      } else if (initialData.actionType === 'game_rating') {
        targetKey = 'game';
      } else if (initialData.actionType === 'badge_claim') {
        targetKey = 'badge';
      } else if (initialData.actionType === 'survey') {
        targetKey = 'survey';
      } else if (initialData.actionType === 'event_notification') {
        targetKey = 'event';
      }

      if (targetKey && currentOption.value) {
        setOptionsState(prev => {
          const currentOptions = prev[targetKey!] || [];
          const exists = currentOptions.some(option => option.value === currentOption.value);
          if (!exists) {
            return {
              ...prev,
              [targetKey!]: [currentOption, ...currentOptions]
            };
          }
          return prev;
        });
      }
    }
  }, [isEdit, initialData?.targetId, initialData?.actionType]); // Simplificado: solo targetId

  // Observar el valor del actionType para mostrar/ocultar el selector
  const watchedActionType = watch('actionType');
  const watchedTargetId = watch('targetId');

  // Resetear campos cuando cambia el actionType
  useEffect(() => {
    // Solo resetear si no estamos en modo edición inicial
    if (!isEdit || (isEdit && watchedActionType !== initialData?.actionType)) {
      // Obtener valores actuales
      const currentValues = getValues();
      
      // Resetear con valores actuales pero limpiando targetId y streakDays
      reset({
        ...currentValues,
        targetId: undefined,
        streakDays: undefined,
        minAmount: undefined,
        articleId: undefined
      });
    }
  }, [watchedActionType, reset, getValues, isEdit, initialData?.actionType]);

  const beforeSubmit = async (data: SeasonChallengeTaskFormData) => {
    // Calcular targetType dinámicamente basado en actionType y targetId
    let targetType = '';
    if (data.targetId) {
      const config = getSelectorConfig(data.actionType);
      targetType = config?.targetType || '';
    }

    // Preparar actionParams según el tipo de acción
    let actionParams = {};
    if (data.actionType === 'streak' && (data as any).streakDays) {
      actionParams = {
        days: parseInt((data as any).streakDays, 10)
      };
    } else if (data.actionType === 'shop_purchase' && (data as any).minAmount) {
      actionParams = {
        minAmount: parseFloat((data as any).minAmount)
      };
    } else if (data.actionType === 'news_vote') {
      actionParams = {
        articleId: data.articleId
      }
    }
    // Para cualquier otro actionType, actionParams será un objeto vacío

    const finalData: SeasonChallengeTaskFormData = {
      ...data,
      // Solo incluir targetType si hay un targetId seleccionado
      targetType: data.targetId ? targetType : undefined,
      // Incluir actionParams con days para streak
      actionParams,
      extra_data: {
        ...data.extra_data,
        ...(otherLangs && {
          en: {
            title: data.extra_data?.en?.title || '',
          },
          pt: {
            title: data.extra_data?.pt?.title || '',
          },
          translated: true
        })
      }
    };
    await onSubmit(finalData);
  };

  return (
    <OlaggForm
      onSubmit={beforeSubmit}
      useFormMethods={useFormMethods}
      style={{
        width: '100%'
      }}
    >
      <VStack color={'white'} w={'full'} maxW='600px' margin='0 auto' spacing={6}>

        {/* Título */}
        <Box w="full">
          <OlaGGFormInput
            controlName="title"
            label="Título"
            placeholder="Ingresa el título de la tarea"
            required={true}
          />
        </Box>

        {/* Tipo de Acción */}
        <Box w="full">
          <OlaggFormSelect
            controlName="actionType"
            label="Tipo de Acción"
            placeholder="Selecciona el tipo de acción"
            staticOptions={actionTypeOptions}
            required={true}
            newVersion
          />
        </Box>

        {/* Campo específico para Streak - Días */}
        {watchedActionType === 'streak' && (
          <Box w="full">
            <OlaGGFormInput
              controlName="streakDays"
              label="Número de Días de Racha"
              placeholder="Ingresa el número de días (ej: 7)"
              required={true}
            />
          </Box>
        )}

        {/* Campo específico para ShopPurchase - Monto Mínimo */}
        {watchedActionType === 'shop_purchase' && (
          <Box w="full">
            <OlaGGFormInput
              controlName="minAmount"
              label="Monto Mínimo"
              placeholder="Ingresa el monto mínimo (ej: 10.50)"
              required={false}
            />
          </Box>
        )}

        {/* Campo específico para NewsVote - Artículo ID */}
        {watchedActionType === 'news_vote' && (
          <Box w="full">
            <OlaGGFormInput
              controlName="articleId"
              label="ID del Artículo en NewsRoom"
              placeholder="Ingresa el ID del artículo"
              required={false}
            />
          </Box>
        )}

        {/* Target ID - Selector dinámico según actionType */}
        {(() => {
          const config = getSelectorConfig(watchedActionType);
          
          if (!config) return null;
          
          return (
            <Box w="full">
              <OlaggFormSelect
                controlName="targetId"
                label={config.label}
                placeholder={config.placeholder}
                staticOptions={optionsState[config.key] || []}
                asyncOptions={config.asyncOptions}
                isSearchable={true}
                newVersion
                required={config.required}
              />
            </Box>
          );
        })()}

        <OlaGGFormInput
          controlName="link"
          label="Link (opcional)"
          placeholder="/quests"
          required={false}
        />

        {/* Switch para otros idiomas */}
        <Flex
          w="full"
          justify="start"
          align="center"
        >
          <Text color="white" style={{ cursor: 'pointer' }}>
            Otros idiomas
          </Text>
          <Switch
            colorScheme="purple"
            isChecked={otherLangs}
            size="md"
            ml={2}
            onChange={() => setOtherLangs(prev => !prev)}
          />
        </Flex>

        {/* Campos multiidioma */}
        {otherLangs && (
          <>
            <Divider />
            <Text fontSize="lg" fontWeight="bold" w="full" textAlign="left">
              Traducciones
            </Text>
            
            {/* Inglés */}
            <Box w="full">
              <OlaGGFormInput
                controlName="extra_data.en.title"
                label="Título en Inglés"
                placeholder="Task title in English"
              />
            </Box>

            {/* Portugués */}
            <Box w="full">
              <OlaGGFormInput
                controlName="extra_data.pt.title"
                label="Título en Portugués"
                placeholder="Título da tarefa em português"
              />
            </Box>
          </>
        )}

        <Button
          variant={'filledGradient'}
          type="submit"
          w={'full'}
          style={{
            marginTop: '30px'
          }}
          isLoading={loading}
          loadingText={isEdit ? 'Actualizando...' : 'Creando...'}
        >
          {isEdit ? 'Actualizar Tarea' : 'Crear Tarea'}
        </Button>

        <Button
          variant="outline"
          w={'full'}
          onClick={onCancel}
          isDisabled={loading}
        >
          Cancelar
        </Button>
      </VStack>
    </OlaggForm>
  );
};

export default TaskForm;
export { TaskForm as Form };
